import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import {
    ANALYSIS_COOKIES_PROPERTY_NAME,
    COOKIE_CONSENT_CHANGED_EVENT,
    COOKIE_CONSENT_LIFETIME_DAYS,
    CookieStateListType,
    ESSENTIAL_COOKIES_PROPERTY_NAME,
    FUNCTIONAL_COOKIES_PROPERTY_NAME
} from '../constants/cookie-constants';
import { cookieStorage } from '../helpers/cookie-helper';

type UseCookiesReturn = {
    cookiesState: CookieStateListType;
    essentialCookiesPermissionState: boolean;
    functionalCookiesPermissionState: boolean;
    analysisCookiesPermissionState: boolean;
    setFunctionalCookiesPermissionState: Dispatch<SetStateAction<boolean>>;
    setAnalysisCookiesPermissionState: Dispatch<SetStateAction<boolean>>;
    acceptAllOptionalCookies: () => void;
    rejectAllOptionalCookies: () => void;
    save: () => void;
};

export default function useCookies(): UseCookiesReturn {
    const [essentialCookiesPermissionState] = useState<boolean>(
        cookieStorage.getItem(ESSENTIAL_COOKIES_PROPERTY_NAME) ?? true
    );
    const [functionalCookiesPermissionState, setFunctionalCookiesPermissionState] = useState<boolean>(
        cookieStorage.getItem(FUNCTIONAL_COOKIES_PROPERTY_NAME) ?? true
    );
    const [analysisCookiesPermissionState, setAnalysisCookiesPermissionState] = useState<boolean>(
        cookieStorage.getItem(ANALYSIS_COOKIES_PROPERTY_NAME) ?? true
    );

    const liveCookieStates = {
        essentialCookiesPermissionState: cookieStorage.getItem(ESSENTIAL_COOKIES_PROPERTY_NAME) ?? true,
        functionalCookiesPermissionState: cookieStorage.getItem(FUNCTIONAL_COOKIES_PROPERTY_NAME) ?? true,
        analysisCookiesPermissionState: cookieStorage.getItem(ANALYSIS_COOKIES_PROPERTY_NAME) ?? true
    };

    const cookiesState: CookieStateListType = {
        essentialCookieState: {
            string: ESSENTIAL_COOKIES_PROPERTY_NAME,
            value: true
        },
        functionalCookieState: {
            string: FUNCTIONAL_COOKIES_PROPERTY_NAME,
            value: functionalCookiesPermissionState
        },
        analysisCookieState: {
            string: ANALYSIS_COOKIES_PROPERTY_NAME,
            value: analysisCookiesPermissionState
        }
    };

    function acceptAllOptionalCookies() {
        cookiesState.functionalCookieState.value = true;
        cookiesState.analysisCookieState.value = true;

        setFunctionalCookiesPermissionState(true);
        setAnalysisCookiesPermissionState(true);

        saveCookiesPreferences();
    }

    function rejectAllOptionalCookies() {
        cookiesState.functionalCookieState.value = false;
        cookiesState.analysisCookieState.value = false;

        setFunctionalCookiesPermissionState(false);
        setAnalysisCookiesPermissionState(false);

        saveCookiesPreferences();
    }

    function saveCookiesPreferences() {
        // Both options matter. Without `expires` the browser keeps these only for the session, so
        // the banner came back on the next visit; without `path` the cookie is scoped to the
        // directory of whatever page the visitor happened to consent on, so it would not be found
        // again elsewhere on the site.
        const expires = new Date(Date.now() + COOKIE_CONSENT_LIFETIME_DAYS * 24 * 60 * 60 * 1000);
        Object.keys(cookiesState).forEach((key) => {
            const cookie = cookiesState[key as keyof typeof cookiesState];
            cookieStorage.setItem(cookie.string, cookie.value, { expires, path: '/' });
        });
        // Consent-gated integrations (e.g. analytics trackers) listen for this to react without a reload.
        window.dispatchEvent(new Event(COOKIE_CONSENT_CHANGED_EVENT));
    }

    useEffect(() => {
        const functionalCookiesPropertyValue = liveCookieStates.functionalCookiesPermissionState;
        const analysisCookiesPropertyName = liveCookieStates.analysisCookiesPermissionState;
        setFunctionalCookiesPermissionState(functionalCookiesPropertyValue);
        setAnalysisCookiesPermissionState(analysisCookiesPropertyName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(liveCookieStates)]);

    return {
        cookiesState: cookiesState,
        essentialCookiesPermissionState: essentialCookiesPermissionState,
        functionalCookiesPermissionState: functionalCookiesPermissionState,
        analysisCookiesPermissionState: analysisCookiesPermissionState,
        setFunctionalCookiesPermissionState: setFunctionalCookiesPermissionState,
        setAnalysisCookiesPermissionState: setAnalysisCookiesPermissionState,
        acceptAllOptionalCookies: acceptAllOptionalCookies,
        rejectAllOptionalCookies: rejectAllOptionalCookies,
        save: saveCookiesPreferences
    };
}
